// AG-MEMORY: where a helper's own Recall key stands on its container, as a small status chip on
// its card (the DE's Recall panel shows the same for the DE). Detail sits behind an (i); nothing
// shows for a helper without a Recall agent (memory off, or not provisioned yet).
import { Badge, InfoTip } from "@companyos/ui";
import type { WorkforceAgentRow } from "../../api/types";

type State = NonNullable<WorkforceAgentRow["recall_keys"]>["state"];

const CHIP: Record<
  State,
  { label: string; tone: "success" | "warning" | "neutral" | "danger"; text: string }
> = {
  applied: {
    label: "Memory applied",
    tone: "success",
    text: "The container holds this helper's own memory key.",
  },
  pending: {
    label: "Memory pending",
    tone: "warning",
    text: "The key was sent; the container has not applied it yet. It catches up on the next deploy.",
  },
  unknown_profile: {
    label: "Unknown profile",
    tone: "danger",
    text: "The container knows no profile with the name stored on this helper, so it holds no memory key. Check the runtime profile.",
  },
  no_profile: {
    label: "No runtime profile",
    tone: "neutral",
    text: "This helper has no runtime profile stored, so there is nowhere to send its memory key.",
  },
  waiting: {
    label: "Memory waiting",
    tone: "neutral",
    text: "The key has not been sent to a container yet.",
  },
};

export function RecallKeysChip({ agent }: { agent: Pick<WorkforceAgentRow, "recall_keys"> }) {
  const s = agent.recall_keys;
  if (!s) return null;
  const meta = CHIP[s.state];
  return (
    <span className="inline-flex shrink-0 items-center gap-0.5" data-slot="recall-keys">
      <Badge variant={meta.tone} data-recall-keys={s.state}>
        {meta.label}
      </Badge>
      <InfoTip label="About the memory key status">{meta.text}</InfoTip>
    </span>
  );
}
